import { useMemo, useState } from 'react';
import {
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isToday,
  isSameDay,
  isWeekend
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { PlanningBadgeGrouped } from './PlanningBadgeGrouped';
import { ChantierIcon } from '@/components/chantiers/ChantierIcon';
import { EquipeIcon } from '@/components/equipe/EquipeIcon';
import { cn } from '@/lib/utils';
import { formatHoursForDay } from '@/lib/heures-utils';
import { X, Users, MapPin, Calendar, Clock, UsersRound } from 'lucide-react';
import type { CalendarAffectation } from '@/types/calendar';

// Type pour les affectations groupées par chantier
interface GroupedChantier {
  chantier: CalendarAffectation['chantier'];
  memberCount: number;
  affectations: CalendarAffectation[];
}

// Calcule le nombre de jours ouvrés (lun-ven) entre deux dates
function countBusinessDays(startDate: Date, endDate: Date): number {
  let count = 0;
  const current = new Date(startDate);
  while (current <= endDate) {
    if (!isWeekend(current)) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }
  return count;
}

/**
 * Vérifie si une affectation doit être affichée pour un jour donné.
 * Utilise jours_details (JSONB) si disponible pour une précision exacte,
 * sinon utilise la logique classique (plage de dates + jours ouvrés).
 */
function shouldShowOnDay(aff: CalendarAffectation, day: Date): boolean {
  const dateStr = format(day, 'yyyy-MM-dd');

  // Normaliser les dates pour ignorer les heures (comparaison par chaîne YYYY-MM-DD)
  const dayStr = dateStr;
  const debutStr = aff.date_debut.split('T')[0]; // Prendre uniquement la partie date
  const finStr = aff.date_fin.split('T')[0];

  // Vérifier d'abord si le jour est dans la plage de dates (comparaison par chaîne)
  if (dayStr < debutStr || dayStr > finStr) {
    return false;
  }

  // Si jours_details est disponible, utiliser cette info précise
  if (aff.jours_details && Array.isArray(aff.jours_details) && aff.jours_details.length > 0) {
    // Chercher si ce jour est dans jours_details
    return aff.jours_details.some(jd => jd.date === dateStr);
  }

  // Fallback: logique classique pour les affectations sans jours_details
  // Pour les weekends, vérifier si jours_travailles > jours ouvrés
  if (isWeekend(day)) {
    const debut = new Date(debutStr);
    const fin = new Date(finStr);
    const joursOuvresTheoriques = countBusinessDays(debut, fin);
    const joursTravailles = aff.jours_travailles || 0;
    return joursTravailles > joursOuvresTheoriques;
  }

  // Jour de semaine dans la plage = afficher
  return true;
}

interface PlanningWeekViewProps {
  currentDate: Date;
  affectations: CalendarAffectation[];
  selectedDate?: Date;
  onDayClick?: (date: Date) => void;
}

export function PlanningWeekView({
  currentDate,
  affectations,
  selectedDate,
  onDayClick,
}: PlanningWeekViewProps) {
  // État pour la modal de détail au hover sur un badge
  const [hoveredChantier, setHoveredChantier] = useState<{ day: Date; grouped: GroupedChantier } | null>(null);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);

  // Gestion du hover avec délai sur le badge chantier
  const handleBadgeMouseEnter = (day: Date, grouped: GroupedChantier) => {
    // Clear any existing timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    // Délai de 300ms avant d'afficher la modal
    const timeout = setTimeout(() => {
      setHoveredChantier({ day, grouped });
    }, 300);
    setHoverTimeout(timeout);
  };

  const handleBadgeMouseLeave = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  };

  const closeHoverModal = () => {
    setHoveredChantier(null);
  };

  // Jours de la semaine (Lundi → Dimanche)
  const weekDays = useMemo(() => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    const end = endOfWeek(currentDate, { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [currentDate]);

  // Grouper les affectations par jour ET par chantier
  const affectationsByDay = useMemo(() => {
    const grouped: Record<string, GroupedChantier[]> = {};

    weekDays.forEach(day => {
      const dateKey = format(day, 'yyyy-MM-dd');

      // Utiliser la nouvelle fonction shouldShowOnDay qui gère jours_details
      const dayAffectations = affectations.filter(aff => shouldShowOnDay(aff, day));

      // Regrouper par chantier_id
      const byChantier: Record<string, CalendarAffectation[]> = {};
      dayAffectations.forEach(aff => {
        const chantierId = aff.chantier_id;
        if (!byChantier[chantierId]) {
          byChantier[chantierId] = [];
        }
        byChantier[chantierId].push(aff);
      });

      // Transformer en tableau avec info du chantier et nombre de membres
      grouped[dateKey] = Object.values(byChantier).map(chantierAffectations => ({
        chantier: chantierAffectations[0]?.chantier,
        memberCount: chantierAffectations.length,
        affectations: chantierAffectations,
      }));
    });

    return grouped;
  }, [weekDays, affectations]);

  const maxVisibleBadges = 5;

  return (
    <div className="bg-card/30 rounded-2xl border border-border/50 overflow-hidden">
      {/* En-tête des jours */}
      <div className="grid grid-cols-7 bg-muted/30">
        {weekDays.map((day) => {
          const isDayWeekend = isWeekend(day);
          return (
            <div
              key={day.toISOString()}
              className={cn(
                'p-3 text-center border-r border-b border-border/50 last:border-r-0',
                isDayWeekend && 'bg-amber-500/10',
                isToday(day) && 'bg-primary/10'
              )}
            >
              <div className={cn(
                'text-xs font-semibold uppercase tracking-wider',
                isDayWeekend ? 'text-amber-500' : 'text-muted-foreground'
              )}>
                {format(day, 'EEE', { locale: fr })}
              </div>
              <div
                className={cn(
                  'text-2xl font-bold mt-1',
                  isToday(day) ? 'text-primary' : isDayWeekend ? 'text-amber-500' : 'text-foreground'
                )}
              >
                {format(day, 'd')}
              </div>
              <div className={cn(
                'text-xs',
                isDayWeekend ? 'text-amber-500/70' : 'text-muted-foreground'
              )}>
                {format(day, 'MMM', { locale: fr })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Contenu des jours */}
      <div className="grid grid-cols-7">
        {weekDays.map((day, index) => {
          const dateKey = format(day, 'yyyy-MM-dd');
          const dayChantiers = affectationsByDay[dateKey] || [];
          const visibleChantiers = dayChantiers.slice(0, maxVisibleBadges);
          const hiddenCount = dayChantiers.length - maxVisibleBadges;
          const isDayToday = isToday(day);
          const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
          const isDayWeekend = isWeekend(day);
          const isLastCol = index === 6;

          return (
            <button
              key={dateKey}
              onClick={() => onDayClick?.(day)}
              className={cn(
                'min-h-[140px] p-2 border-b border-border/50',
                !isLastCol && 'border-r border-border/50',
                'flex flex-col text-left transition-colors',
                // Weekend : fond ambré léger
                isDayWeekend && 'bg-amber-500/5',
                isDayToday && 'bg-primary/5',
                isSelected && 'ring-2 ring-primary ring-inset bg-primary/10'
              )}
            >
              {/* Badges des chantiers (groupés) - hover sur chaque badge */}
              <div className="flex flex-col gap-1 flex-1">
                {visibleChantiers.map((grouped) => (
                  <div
                    key={grouped.chantier?.id || grouped.affectations[0]?.id}
                    onMouseEnter={() => handleBadgeMouseEnter(day, grouped)}
                    onMouseLeave={handleBadgeMouseLeave}
                  >
                    <PlanningBadgeGrouped
                      chantier={grouped.chantier}
                      memberCount={grouped.memberCount}
                      affectations={grouped.affectations}
                    />
                  </div>
                ))}

                {/* Indicateur "+X autres chantiers" */}
                {hiddenCount > 0 && (
                  <div
                    className={cn(
                      'flex items-center justify-center px-2 py-1 rounded',
                      'bg-muted text-muted-foreground text-xs font-medium'
                    )}
                  >
                    +{hiddenCount} autre{hiddenCount > 1 ? 's' : ''}
                  </div>
                )}

                {/* Message si aucune affectation */}
                {dayChantiers.length === 0 && (
                  <p className="text-xs text-muted-foreground/50 italic mt-2">
                    Aucune affectation
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          MODAL DE DÉTAIL AU HOVER SUR UN BADGE CHANTIER
          ═══════════════════════════════════════════════════════════════ */}
      {hoveredChantier && (
        <>
          {/* Overlay semi-transparent */}
          <div
            className="fixed inset-0 bg-black/40 z-[60] backdrop-blur-sm"
            onClick={closeHoverModal}
          />

          {/* Modal centrale */}
          <div
            className={cn(
              'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[70]',
              'w-[90vw] max-w-[600px] max-h-[80vh]',
              'bg-card border border-border rounded-2xl shadow-2xl',
              'flex flex-col overflow-hidden',
              'animate-in fade-in-0 zoom-in-95 duration-200'
            )}
            onMouseLeave={closeHoverModal}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border bg-muted/30">
              <div className="flex items-center gap-3">
                <div className={cn(
                  "p-2 rounded-lg",
                  isWeekend(hoveredChantier.day) ? "bg-amber-500/20" : "bg-primary/10"
                )}>
                  <Calendar className={cn(
                    "w-5 h-5",
                    isWeekend(hoveredChantier.day) ? "text-amber-500" : "text-primary"
                  )} />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-foreground">
                    {format(hoveredChantier.day, 'EEEE d MMMM yyyy', { locale: fr })}
                  </h3>
                  <p className="text-sm text-foreground/80 font-medium">
                    {hoveredChantier.grouped.memberCount} ouvrier{hoveredChantier.grouped.memberCount > 1 ? 's' : ''} affecté{hoveredChantier.grouped.memberCount > 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <button
                onClick={closeHoverModal}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            {/* Contenu scrollable */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="bg-muted/30 rounded-xl p-4 border border-border/50">
                {/* Info Chantier */}
                <div className="flex items-start gap-3 mb-3">
                  {/* Pictogramme */}
                  <ChantierIcon
                    pictogramme={hoveredChantier.grouped.chantier?.pictogramme || 'Home'}
                    couleur={hoveredChantier.grouped.chantier?.couleur || '#6366f1'}
                    size="lg"
                    showBackground={true}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-foreground text-lg truncate">
                        {hoveredChantier.grouped.chantier?.nom_chantier || 'Chantier inconnu'}
                      </h4>
                      {/* Badge Retard si au moins une affectation est un retard */}
                      {hoveredChantier.grouped.affectations.some(aff => aff.notes?.startsWith('Retard chantier')) && (
                        <span className="shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-500/20 text-orange-500 border border-orange-500/30">
                          Retard
                        </span>
                      )}
                    </div>
                    {hoveredChantier.grouped.chantier?.client && (
                      <p className="text-sm text-foreground/80 font-medium">
                        Client : {hoveredChantier.grouped.chantier.client}
                      </p>
                    )}
                    {hoveredChantier.grouped.chantier?.adresse && (
                      <p className="text-xs text-foreground/70 font-medium flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" />
                        {hoveredChantier.grouped.chantier.adresse}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 rounded-full text-sm font-medium">
                    <Users className="w-4 h-4" />
                    {hoveredChantier.grouped.memberCount}
                  </div>
                </div>

                {/* Équipes affectées */}
                {(() => {
                  // Extraire les équipes uniques pour ce chantier
                  const equipesMap = new Map<string, { id: string; nom: string; pictogramme?: string; couleur?: string }>();
                  hoveredChantier.grouped.affectations.forEach(aff => {
                    if (aff.equipe?.id && !equipesMap.has(aff.equipe.id)) {
                      equipesMap.set(aff.equipe.id, {
                        id: aff.equipe.id,
                        nom: aff.equipe.nom,
                        pictogramme: aff.equipe.pictogramme,
                        couleur: aff.equipe.couleur,
                      });
                    }
                  });
                  const uniqueEquipes = Array.from(equipesMap.values());

                  if (uniqueEquipes.length === 0) return null;

                  return (
                    <div className="border-t border-border/50 pt-3 mb-3">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                        <UsersRound className="w-3 h-3" />
                        Équipe{uniqueEquipes.length > 1 ? 's' : ''} affectée{uniqueEquipes.length > 1 ? 's' : ''}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {uniqueEquipes.map((equipe) => (
                          <div
                            key={equipe.id}
                            className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-card border border-border/30"
                          >
                            <EquipeIcon
                              pictogramme={equipe.pictogramme || 'Wrench'}
                              couleur={equipe.couleur || '#f97316'}
                              size="sm"
                              showBackground={true}
                            />
                            <span className="text-sm font-medium text-foreground">
                              {equipe.nom}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Liste des ouvriers */}
                <div className="border-t border-border/50 pt-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                    Ouvriers affectés
                  </p>
                  <div className="space-y-2">
                    {hoveredChantier.grouped.affectations.map((aff) => (
                      <div
                        key={aff.id}
                        className="flex items-center justify-between bg-card rounded-lg p-2 border border-border/30"
                      >
                        <div className="flex items-center gap-2">
                          {/* Picto équipe du membre */}
                          <EquipeIcon
                            pictogramme={aff.equipe?.pictogramme || 'Wrench'}
                            couleur={aff.equipe?.couleur || '#f97316'}
                            size="sm"
                            showBackground={true}
                          />
                          <div>
                            <p className="font-semibold text-foreground text-sm">
                              {aff.membre?.prenom} {aff.membre?.nom}
                            </p>
                            <p className="text-xs text-foreground/70 font-medium">
                              {aff.membre?.specialite || aff.membre?.poste || 'Ouvrier'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right text-xs text-foreground/80 font-medium">
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatHoursForDay(aff, hoveredChantier.day)}
                          </div>
                          {aff.cout_journalier && (
                            <div className="text-primary font-medium">
                              {Number(aff.cout_journalier).toLocaleString('fr-FR')}€/j
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-border bg-muted/20 text-center">
              <p className="text-xs text-muted-foreground">
                Cliquez pour fermer • Survolez un autre chantier pour changer
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
